import { DataSet, DataSetInformation, Record } from '@mtna/data-core-ts';
import { Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { RdsVariableSelectQuery } from '@rds/rds-base-ts';
import { SelectParameters, VariableSummariesParameters } from '@rds/shared-api';

const RDS_TABLE_MAX_ROWS = 21;
const RDS_TABLE_MAX_COLUMNS = 21;

const RDS_GHOST_RECORD: Record = {
  data: [],
  unit: {
    data: [],
  },
};

const RDS_GHOST_RESULT: DataSet = {
  uri: 'RDS_GHOST_DATA_SET',
  dataSetInformation: {
    columnCount: 0,
  },
  classifications: [],
  name: '',
  recordLayout: {
    variables: [],
    uri: '',
  },
  recordUnit: [],
  totals: [],
  records: [],
};

export class RdsGhostVariable implements Variable {
  static count = 0;
  uri: string;
  dataType = 'GHOST';
  constructor() {
    this.uri = String(RdsGhostVariable.count);
    RdsGhostVariable.count++;
  }
}

export class RdsGhostVariableSummary implements VariableSummary {
  static count = 0;
  classificationId = '';
  classificationUri = '';
  dataType = 'GHOST';
  description = '';
  id: string;
  index: number;
  isDimension = false;
  isMeasure = false;
  isRequired = false;
  isWeight = false;
  keywords = [];
  label = '';
  name = '';
  questionText = '';
  storageType = '';
  uri: string;
  lastUpdate = new Date();
  constructor() {
    this.uri = String(RdsGhostVariableSummary.count);
    this.id = this.uri;
    this.index = RdsGhostVariableSummary.count;
    RdsGhostVariableSummary.count++;
  }
}

/**
 * Generate ghost search results to show while loading.
 *
 * @param newParams New search parameters
 * @param lastParams Last search parameters
 * @param columnCount Total number of columns
 * @param rowCount Total number of rows
 * @returns Ghost DataSet
 */
export function rdsGenerateGhostResults(
  newParams: SelectParameters,
  lastParams: SelectParameters,
  columnCount: number,
  rowCount: number
): DataSet {
  const params = { ...lastParams, ...newParams };
  const dataSetInformation: DataSetInformation = {
    ...RDS_GHOST_RESULT.dataSetInformation,
    columnLimit: params.collimit,
    columnOffset: params.coloffset,
    rowLimit: params.limit,
    rowOffset: params.offset,
  };
  // Create fake number of variables equal to columns requested
  const variables: Array<Variable> = [];
  // Determine number of columns requested
  const numCols = columnCount
    ? Math.min(columnCount - (params.coloffset || 0), params.collimit || 0, RDS_TABLE_MAX_COLUMNS)
    : Math.min(params.collimit || 0, RDS_TABLE_MAX_COLUMNS);
  for (let i = 0; i < numCols; i++) {
    variables.push(new RdsGhostVariable());
  }

  // Create fake number of records equal to rows requested
  const records: Array<Record> = [];
  // Determine number of rows requested
  const numRows = rowCount
    ? Math.min(rowCount - (params.offset || 20), params.limit || 0, RDS_TABLE_MAX_ROWS)
    : Math.min(params.limit || 0, RDS_TABLE_MAX_ROWS);
  for (let i = 0; i < numRows; i++) {
    records.push(RDS_GHOST_RECORD);
  }
  return { ...RDS_GHOST_RESULT, dataSetInformation, recordLayout: { variables: variables, uri: '' }, records };
}

/**
 * Generate ghost variable summaries
 *
 * @param newParams New search parameters
 * @param lastParams Last search parameters
 * @param columnCount Total number of columns
 * @returns Ghost DataSet
 */
export function rdsGenerateGhostVariableSummaries<T extends VariableSummariesParameters | RdsVariableSelectQuery>(
  newParams: T,
  lastParams: T,
  columnCount?: number
): Array<RdsGhostVariableSummary> {
  const params = { ...lastParams, ...newParams };
  // Create fake number of variables equal to columns requested
  const variableSummaries = [];
  // Determine number of columns requested
  const numCols = columnCount
    ? Math.min(columnCount - (params.coloffset || 0), params.collimit || 0, RDS_TABLE_MAX_COLUMNS)
    : Math.min(params.collimit || 0, RDS_TABLE_MAX_COLUMNS);
  for (let i = 0; i < numCols; i++) {
    variableSummaries.push(new RdsGhostVariableSummary());
  }
  return variableSummaries;
}
